import { supabase } from '@/lib/supabaseClient';

/**
 * Canonical column allowlist for the `clients` Supabase table used by both
 * the lib loader and the DataContext cache. Keeping a single source of truth
 * means the cached payload satisfies every consumer (Clients/Leads list,
 * contact profile sheet, lead pipeline, dashboard activity feed, etc.).
 */
export const CLIENT_FIELDS =
  'id,name,phone,email,address,address_street,address_city,address_state,address_zip,' +
  'type,source,ltv,lead_value,lead_type,last_contact,notes,created_at,' +
  'lead_status,lead_status_at';

/**
 * Lead outcome statuses. Deliberately no "lost" — a lead that doesn't convert
 * is "learned," not lost.
 */
export const LEAD_STATUSES = ['pending', 'won', 'learned'];

export const LEAD_STATUS_META = {
  pending: { label: 'Pending', tone: 'warning' },
  won: { label: 'Won', tone: 'success' },
  learned: { label: 'Learned', tone: 'info' },
};

/** Normalize a contact's lead status (null-safe): clients default to won. */
export const getLeadStatus = (contact) =>
  contact?.lead_status || (contact?.type === 'client' ? 'won' : 'pending');

/**
 * Compose a freeform "address" string from structured parts. The legacy
 * `clients.address` column is retained for backward compat (read by older
 * code paths). This helper keeps it in sync whenever the structured fields
 * change.
 */
export const composeClientAddress = ({ address_street, address_city, address_state, address_zip } = {}) => {
  const street = (address_street || '').trim();
  const city   = (address_city   || '').trim();
  const state  = (address_state  || '').trim();
  const zip    = (address_zip    || '').trim();
  const cityStateZip = [city, [state, zip].filter(Boolean).join(' ').trim()].filter(Boolean).join(', ');
  return [street, cityStateZip].filter(Boolean).join(', ');
};

/**
 * Currency formatting helper used across the Clients & Leads UI.
 */
export const formatUsd = (value, opts = {}) => {
  const n = Number(value);
  const safe = Number.isFinite(n) ? n : 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: opts.fraction ?? 0,
    minimumFractionDigits: opts.fraction ?? 0,
  }).format(safe);
};

/**
 * Build initials for an avatar fallback from a contact's display name.
 */
export const getInitials = (name) => {
  if (!name) return '??';
  const parts = String(name).trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

/**
 * Compute the stats bar values:
 *   - clients (count)
 *   - leads (count)
 *   - pipelineValue: sum of lead_value across leads (open opportunities)
 *   - lifetimeValue: sum of ltv across clients (closed lifetime revenue)
 */
export const computeContactStats = (contacts = []) => {
  let clients = 0;
  let leads = 0;
  let pipelineValue = 0;
  let lifetimeValue = 0;
  for (const c of contacts) {
    if (c?.type === 'lead') {
      leads += 1;
      pipelineValue += Number(c?.lead_value) || 0;
    } else if (c?.type === 'client') {
      clients += 1;
      lifetimeValue += Number(c?.ltv) || 0;
    }
  }
  return { clients, leads, pipelineValue, lifetimeValue, total: contacts.length };
};

/**
 * Apply the current tab + search filter and sort.
 */
export const applyContactFilter = (contacts = [], { tab = 'clients', search = '', sort = 'name-asc' } = {}) => {
  const q = (search || '').trim().toLowerCase();

  let list = contacts.slice();
  if (tab === 'clients') list = list.filter((c) => c?.type === 'client');
  else if (tab === 'leads') list = list.filter((c) => c?.type === 'lead');

  if (q) {
    list = list.filter((c) => {
      const hay = [
        c?.name,
        c?.phone,
        c?.email,
        c?.address,
        c?.lead_type,
        c?.source,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }

  const cmp = (a, b) => {
    switch (sort) {
      case 'name-desc':
        return String(b?.name || '').localeCompare(String(a?.name || ''));
      case 'value-desc': {
        const av = Number(a?.lead_value || a?.ltv || 0);
        const bv = Number(b?.lead_value || b?.ltv || 0);
        return bv - av;
      }
      case 'recent':
        return new Date(b?.created_at || 0) - new Date(a?.created_at || 0);
      case 'name-asc':
      default:
        return String(a?.name || '').localeCompare(String(b?.name || ''));
    }
  };

  return list.sort(cmp);
};

/**
 * Find jobs that belong to a given contact, matching by client_id OR (legacy) by name.
 */
export const findJobsForContact = (contact, jobs = []) => {
  if (!contact) return [];
  const id = contact.id;
  const name = (contact.name || '').trim().toLowerCase();
  return jobs.filter((j) => {
    if (j?.client_id && j.client_id === id) return true;
    if (!j?.client) return false;
    return String(j.client).trim().toLowerCase() === name;
  });
};

/**
 * Fetch all contacts (clients + leads) plus jobs for profile drilldown.
 */
export const fetchContactsData = async () => {
  const [contactsRes, jobsRes] = await Promise.all([
    supabase
      .from('clients')
      .select(CLIENT_FIELDS)
      .order('created_at', { ascending: false }),
    supabase
      .from('jobs')
      .select(
        'id,name,client,client_id,address,contract,co_total,phase,status,started,est_end,job_type,invoices'
      )
      .order('created_at', { ascending: false }),
  ]);
  if (contactsRes.error) throw contactsRes.error;
  if (jobsRes.error) throw jobsRes.error;
  return {
    contacts: contactsRes.data || [],
    jobs: jobsRes.data || [],
  };
};

/**
 * Insert a new lead row.
 */
export const insertLead = async (payload) => {
  const safe = sanitizeContactPayload(payload, { defaultType: 'lead' });
  if (!safe.lead_status) {
    safe.lead_status = 'pending';
    safe.lead_status_at = new Date().toISOString();
  }
  const { data, error } = await supabase
    .from('clients')
    .insert([safe])
    .select(CLIENT_FIELDS)
    .single();
  if (error) throw error;
  return data;
};

/**
 * Insert a new client (used when adding a confirmed customer directly).
 */
export const insertClient = async (payload) => {
  const safe = sanitizeContactPayload(payload, { defaultType: 'client' });
  if (!safe.lead_status) {
    safe.lead_status = 'won';
    safe.lead_status_at = new Date().toISOString();
  }
  const { data, error } = await supabase
    .from('clients')
    .insert([safe])
    .select(CLIENT_FIELDS)
    .single();
  if (error) throw error;
  return data;
};

/**
 * Patch an existing contact.
 */
export const updateContact = async (id, updates) => {
  const safe = sanitizeContactPayload(updates, { partial: true });
  const { data, error } = await supabase
    .from('clients')
    .update(safe)
    .eq('id', id)
    .select(CLIENT_FIELDS)
    .single();
  if (error) throw error;
  return data;
};

/**
 * Delete a contact.
 */
export const deleteContact = async (id) => {
  const { error } = await supabase.from('clients').delete().eq('id', id);
  if (error) throw error;
  return true;
};

/**
 * Set a contact's lead status (WON / LEARNED / PENDING) and stamp the change
 * time. Returns the updated row.
 */
export const setLeadStatus = async (id, status) => {
  if (!id) throw new Error('Missing contact id');
  if (!LEAD_STATUSES.includes(status)) {
    throw new Error(`Invalid lead status: ${status}`);
  }
  return updateContact(id, {
    lead_status: status,
    lead_status_at: new Date().toISOString(),
  });
};

/**
 * Convert a lead row into a client. Moves lead_value -> ltv and clears the
 * lead-side fields. Idempotent (no-op if already a client).
 */
export const convertLeadToClient = async (lead) => {
  if (!lead?.id) throw new Error('Missing lead id');
  if (lead.type === 'client') return lead;
  const newLtv = (Number(lead.ltv) || 0) + (Number(lead.lead_value) || 0);
  const updates = {
    type: 'client',
    ltv: newLtv,
    lead_value: 0,
    lead_status: 'won',
    lead_status_at: new Date().toISOString(),
  };
  return updateContact(lead.id, updates);
};

/**
 * Trim and coerce a contact payload before sending it to Supabase.
 */
function sanitizeContactPayload(input = {}, { defaultType, partial = false } = {}) {
  const out = {};
  const stringFields = [
    'name',
    'phone',
    'email',
    'address',
    'address_street',
    'address_city',
    'address_state',
    'address_zip',
    'lead_type',
    'source',
    'last_contact',
  ];
  for (const k of stringFields) {
    if (k in input) {
      const v = input[k];
      out[k] = v === null || v === undefined ? null : String(v).trim();
    }
  }
  // Keep the legacy freeform `address` column in sync with the structured
  // fields whenever any structured part is provided. Older readers
  // (jobs/estimates lookups) still consume `address`.
  const hasStructuredAddress =
    'address_street' in input ||
    'address_city' in input ||
    'address_state' in input ||
    'address_zip' in input;
  if (hasStructuredAddress) {
    const composed = composeClientAddress({
      address_street: out.address_street ?? input.address_street ?? '',
      address_city:   out.address_city   ?? input.address_city   ?? '',
      address_state:  out.address_state  ?? input.address_state  ?? '',
      address_zip:    out.address_zip    ?? input.address_zip    ?? '',
    });
    out.address = composed || null;
  }
  if ('type' in input) {
    out.type = input.type === 'client' ? 'client' : 'lead';
  } else if (!partial && defaultType) {
    out.type = defaultType;
  }
  if ('lead_value' in input) {
    const n = Number(input.lead_value);
    out.lead_value = Number.isFinite(n) && n >= 0 ? n : 0;
  }
  if ('ltv' in input) {
    const n = Number(input.ltv);
    out.ltv = Number.isFinite(n) && n >= 0 ? n : 0;
  }
  if ('notes' in input) {
    if (Array.isArray(input.notes)) {
      out.notes = input.notes.map((s) => String(s)).filter((s) => s.length);
    } else if (typeof input.notes === 'string') {
      out.notes = input.notes
        .split(/\n+/)
        .map((s) => s.trim())
        .filter(Boolean);
    } else {
      out.notes = [];
    }
  }
  if ('lead_status' in input) {
    out.lead_status = LEAD_STATUSES.includes(input.lead_status)
      ? input.lead_status
      : 'pending';
  }
  if ('lead_status_at' in input) {
    out.lead_status_at = input.lead_status_at
      ? String(input.lead_status_at)
      : null;
  }
  return out;
}
